import { type QueryKey, keepPreviousData } from '@tanstack/react-query';

import { type PaginationResponse } from '@/models/pagination';

import { useAppQuery } from './useAppQuery';
import { usePageParam } from './usePageParam';

interface PageSlice {
  limit: number;
  page: number;
  offset: number;
}

/** The slice of a list one page reads: `page` is 1-based, as in the URL. */
export const pageSlice = (page: number, limit: number): PageSlice => ({
  limit,
  page,
  offset: (page - 1) * limit,
});

/** The cache key of one page of a list — shared with loaders warming it. */
export const paginatedQueryKey = (queryKey: QueryKey, slice: PageSlice) => [
  ...(Array.isArray(queryKey) ? (queryKey as unknown as QueryKey) : [queryKey]),
  slice,
];

interface UsePaginatedListOptions {
  limit?: number;
  page?: number;
}
export function usePaginatedList<TData extends PaginationResponse>(
  queryKey: QueryKey,
  fetchFn: (pagination: { limit: number; offset: number }) => Promise<TData>,
  options?: UsePaginatedListOptions,
) {
  const { page: urlPage, setPage } = usePageParam();
  const page = options?.page ?? urlPage;
  const slice = pageSlice(page, options?.limit ?? 10);
  const { limit, offset } = slice;

  const query = useAppQuery({
    queryKey: paginatedQueryKey(queryKey, slice),
    queryFn: () => {
      return fetchFn({ limit, offset });
    },
    placeholderData: keepPreviousData,
  });

  return {
    ...query,
    page,
    limit,
    offset,
    setPage,
  };
}
